import * as React from 'react';
import { FormControl } from '@common/components/form-control';
import Validator from 'validatorjs';
import cx from 'classnames';
import { I18n } from 'react-redux-i18n';

import { Link } from '@common/components/Link';
import { LOOKUP_MODULE_NAME } from '../../LookupTable';

import './HyperLink.styles.scss';

const MODE = {
  editing: 'editing',
  viewing: 'viewing',
};

export const HyperLink = (props) => {
  const { value, defaultValue, className, index, id, autoFocus, placeholder, metadata, onSave, validateValue = () => true } = props;
  const normalizedValue = value || defaultValue || '';
  const [savedValue, setSavedValue] = React.useState(normalizedValue);
  const [currentValue, setCurrentValue] = React.useState(normalizedValue);
  const [isLinkOpen, setLinkOpenState] = React.useState(false);
  const [mode, setMode] = React.useState(MODE.viewing);
  const [linkCoordinates, setLinkCoordinates] = React.useState({ top: 0, left: 0 });
  const ref = React. useRef();

  const isEmail = React.useMemo(() => {
    const validator = new Validator({ email: savedValue }, { email: 'email|required' });
    const passed = validator.passes();

    return passed;
  }, [savedValue]);

  const linkProps = React.useMemo(() => {
    if (isEmail) {
      return {
        href: `mailto:${savedValue}`
      };
    }

    return {
      href: savedValue,
      target: '_blank',
      rel: 'noopener noreferrer'
    };
  }, [savedValue, isEmail]);

  React.useEffect(() => {
    setSavedValue(normalizedValue);
    setCurrentValue(normalizedValue);
  }, [index, value, id]);
  
  const handleChange = React.useCallback((e) => {
    e.persist();

    const { value } = e.target;

    if (validateValue(value)) {
      setCurrentValue(value);
    }
  }, [validateValue]);

  const handleBlur = React.useCallback(async () => {
    setMode(MODE.viewing);

    try {
      await onSave({
        index,
        id,
        savedValue,
        value: currentValue,
        metadata,
      });

      setSavedValue(currentValue);
    } catch (err) {
      setCurrentValue(savedValue);
    }
  }, [currentValue, index, id, savedValue, onSave]);

  const closeLink = React.useCallback(() => {
    setLinkOpenState(false);
  }, []);

  const openLink = React.useCallback(() => {
    const { bottom, left } = ref.current.getBoundingClientRect();

    setLinkCoordinates({ left, top: bottom - 2 });
    setLinkOpenState(true);
  }, []);

  const handleInputMouseEnter = React.useCallback(() => {
    if (mode !== MODE.editing) {
      openLink();
    }
  }, [mode, openLink]);

  const handleInputClick = React.useCallback(() => {
    closeLink();
    setMode(MODE.editing);
  }, [closeLink]);

  return (
    <div
      className={cx('HyperLink', className)}
      onMouseEnter={handleInputMouseEnter}
      onMouseLeave={closeLink}
    >
      <FormControl
        inputRef={ref}
        autoFocus={autoFocus}
        placeholder={placeholder}
        id={`${LOOKUP_MODULE_NAME}-hyperlink--id-${id}--index-${index}`}
        title={currentValue}
        className="HyperLink__input"
        type="text"
        value={currentValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={handleInputClick}
      />
      {isLinkOpen && savedValue && (
        <div 
          style={linkCoordinates} 
          className="HyperLink__actions" 
          onClick={closeLink}
        >
          <Link 
            title={savedValue}
            {...linkProps}
            className="HyperLink__actions__item"
            onClick={closeLink}
          >
            {isEmail ? savedValue : I18n.t('Open')}
          </Link>
        </div>
      )}
    </div>
  );
};

HyperLink.defaultProps = {
  defaultValue: '',
};